import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { ProfileService } from 'client/services';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { loaderWithAuth } from '../FeedbackForm/FeedbackForm.loader';
import useAuth from 'client/utils/auth/useAuth';

import Button from 'client/components/aria/Button/Button';
import StarRating from '../StarRating/StarRating';
import TextArea from 'client/components/GDS/TextArea/TextArea';
import Radio from 'client/components/GDS/Radio/Radio';
import Select from 'client/components/GDS/Select/Select';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import styles from '../FeedbackForm/FeedbackForm.module.scss';
import { useLocaleAutoSwitch } from 'i18n/localeUtils';

const FeedbackForm = ({ setHideFeedbackPrompt, setFeedbackAlert, feedbackAlert, setFeedbackSavedOk }) => {
    const intl = useIntl();
    const currentLocale = useSelector(state => state?.locale?.current);
    useLocaleAutoSwitch();
    const [hideActivityFeedback, setHideActivityFeedback] = useState(true);
    const [feedbackActivityTypes, setfeedbackActivityTypes] = useState(true);
    const { isAuthenticated } = useAuth();
    const INITIAL_CATEGORY = 'INITIAL_CATEGORY';
    const descriptionMaxLength = 1200;
    const _getAuthenticatedContent = async () => {
        if (isAuthenticated) {
            await loaderWithAuth().then(({ feedbackActivityTypes }) => {
                setfeedbackActivityTypes(feedbackActivityTypes);
            }).catch(() => {
                setfeedbackActivityTypes(null);
            });
        }
    };

    useEffect(() => {
        _getAuthenticatedContent();
    }, []);

    const formik = useFormik({
        initialValues: {
            additionalComments: '',
            activityComments: '',
            activityType: '',
            easeOfUseScore: undefined,
            feedbackType: 'GENERAL',
            ratingScore: undefined,
            recommendationScore: undefined
        },
        validationSchema: Yup.object({
            activityComments: Yup.string().trim().max(descriptionMaxLength, intl.formatMessage({ id: 'feedbackForm.additionalComments.validation.length' }, { characterCount: descriptionMaxLength })),
            additionalComments: Yup.string().trim().max(descriptionMaxLength, intl.formatMessage({ id: 'feedbackForm.additionalComments.validation.length' }, { characterCount: descriptionMaxLength })),
            easeOfUseScore: Yup.number().min(1).max(10).required().positive(),
            ratingScore: Yup.number().min(1).max(10).required().positive(),
            recommendationScore: Yup.number().min(1).max(10).required().positive()
        }),

        onSubmit: values => {
            ProfileService.postFeedback(
                values.additionalComments,
                values.activityComments,
                values.activityType,
                values.easeOfUseScore.toString(),
                values.feedbackType,
                values.ratingScore.toString(),
                values.recommendationScore.toString()
            )
                .then(() => {
                    setFeedbackSavedOk(true);
                    setFeedbackAlert(true);
                    setHideFeedbackPrompt(true);
                }).catch(() => {
                    setFeedbackSavedOk(false);
                });
        }
    });

    const hasEnteredRatings = (formik.values.recommendationScore >= 1 && formik.values.recommendationScore <= 10 &&
        formik.values.ratingScore >= 1 && formik.values.ratingScore <= 10);

    const submitFeedback = () => {
        formik.submitForm();
    };

    const handleValueChange = (event) => {
        const { name, value } = event.target;
        formik.setFieldValue(name, value);
    };

    const handleRatingChange = ({ name, value }) => {
        formik.setFieldValue(name, value);
    };

    const getActivityOption = () => {
        const activityKeys = Object.keys(feedbackActivityTypes?.activityTypes || {});
        return (
            activityKeys.map((activityKey) => (
                <option key={activityKey} value={activityKey}>
                    {feedbackActivityTypes?.activityTypes?.[activityKey]?.[currentLocale]}
                </option>

            ))
        );
    };

    return <form onSubmit={formik.handleSubmit} data-testid="feedbackForm">
        {
            <>
                { !feedbackAlert && <>
                    <h2>{intl.formatMessage({ id: 'feedbackForm.subTitle' })}</h2>
                    <StarRating
                        id="ratingScore"
                        label={intl.formatMessage({ id: 'feedbackForm.ratingTitle' })}
                        ariaLabel={intl.formatMessage({ id: 'feedbackForm.ariaSatisfactionLabel' })}
                        numberOfStars={10}
                        onChange={handleRatingChange}
                        data-testid="feedback.ratingScore"
                        highRating={intl.formatMessage({ id: 'feedbackForm.excellent' })}
                        lowRating={intl.formatMessage({ id: 'feedbackForm.veryPoor' })}
                    />
                    <StarRating
                        id="easeOfUseScore"
                        label={intl.formatMessage({ id: 'feedbackForm.easeOfUseTitle' })}
                        ariaLabel={intl.formatMessage({ id: 'feedbackForm.ariaRecommendationLabel' })}
                        numberOfStars={10}
                        onChange={handleRatingChange}
                        data-testid="feedback.easeOfUseScore"
                        highRating={intl.formatMessage({ id: 'feedbackForm.veryEasy' })}
                        lowRating={intl.formatMessage({ id: 'feedbackForm.veryDifficult' })}
                    />
                    <StarRating
                        id="recommendationScore"
                        label={intl.formatMessage({ id: 'feedbackForm.recommendTitle' })}
                        ariaLabel={intl.formatMessage({ id: 'feedbackForm.ariaRecommendationLabel' })}
                        numberOfStars={10}
                        onChange={handleRatingChange}
                        data-testid="feedback.recommendationScore"
                        highRating={intl.formatMessage({ id: 'feedbackForm.veryLikely' })}
                        lowRating={intl.formatMessage({ id: 'feedbackForm.notLikely' })}
                    />
                    <TextArea
                        id="additionalComments"
                        name="additionalComments"
                        label={intl.formatMessage({ id: 'feedbackForm.generalFeedback' })}
                        maxLength={descriptionMaxLength}
                        onChange={handleValueChange}
                        data-testid="feedback.additionalComments"
                    />
                    <h4 className="govuk-label">{intl.formatMessage({ id: 'feedbackForm.additional.feedback' })}</h4>
                    <div className={[styles.radio, 'govuk-radios govuk-radios--inline'].join(' ')} data-module="govuk-radios">
                        <Radio
                            name="additionalFeedback"
                            id={'yes.button'}
                            label={intl.formatMessage({ id: 'yes' })}
                            value={'show'}
                            onChange={(e) => setHideActivityFeedback(e.nativeEvent.target.value === 'hide')}
                        />
                        <Radio
                            name="additionalFeedback"
                            id={'no.button'}
                            label={intl.formatMessage({ id: 'no' })}
                            value={'hide'}
                            onChange={(e) => setHideActivityFeedback(e.nativeEvent.target.value === 'hide')}
                        />
                    </div>
                    { !hideActivityFeedback &&
                        <>
                            <div className="govuk-grid-row">
                                <div className="govuk-grid-column-two-thirds">
                                    <Select
                                        label={intl.formatMessage({ id: 'feedbackForm.activityType.label' })}
                                        name='activityType'
                                        data-testid={'feedback.activity.type'}
                                        onChange={handleValueChange}
                                        // errorMessage={getErrorMessage('activityType', 'feedbackForm.chooseActivity')}
                                        value={formik.activityType}
                                    >
                                        <option value={INITIAL_CATEGORY}>{intl.formatMessage({ id: 'feedbackForm.chooseActivity' })}</option>
                                        {getActivityOption()}
                                    </Select>
                                </div>
                            </div>
                            <TextArea
                                id="activityComments"
                                name="activityComments"
                                label={intl.formatMessage({ id: 'feedbackForm.activityComments' })}
                                maxLength={descriptionMaxLength}
                                onChange={handleValueChange}
                                data-testid="feedback.activityComments"
                            />
                        </> }
                    <Button
                        data-testid="feedbackForm.submitButton"
                        onPress={submitFeedback}
                        isDisabled={!hasEnteredRatings}
                        variant='success'>{intl.formatMessage({ id: 'feedbackForm.submitButton' })}
                    </Button>
                </> }
            </>
        }
    </form>;
};

FeedbackForm.propTypes = {
    setHideFeedbackPrompt: PropTypes.func,
    setFeedbackSavedOk: PropTypes.func,
    setFeedbackAlert: PropTypes.func,
    feedbackAlert: PropTypes.bool
};

FeedbackForm.defaultProps = {
    setHideFeedbackPrompt: () => {},
    setFeedbackSavedOk: () => {},
    setFeedbackAlert: () => {},
    feedbackAlert: false
};

export default FeedbackForm;
